import rp from "@reporting/rp-app-host";
import { AccountingGroup } from "@reporting/rp-types/core";
import { SsoOidcPreset } from "../types";

/**
 * A possibly relative URL made absolute against the current page. The production build sets
 * clientAuthUrl to "/auth", and these URLs are copied into identity providers, so they must
 * carry the origin.
 */
function absolute(url: string): string {
	return new URL(url, window.location.origin).toString().replace(/\/$/, '');
}

/** The login application of the group as an absolute URL: its custom domain, otherwise this origin */
export function getBaseAuthUrl(group?: AccountingGroup): string {
	if (group?.custom_domain) {
		return `https://${group.custom_domain}/auth`;
	}
	return absolute(rp.options.clientAuthUrl || '/auth');
}

export function getCustomAuthUrl(group: AccountingGroup): string {
	const customLoginGroupName = group.sanitized_name || group.custom_domain;
	if (customLoginGroupName) {
		return `${getBaseAuthUrl(group)}/login/${customLoginGroupName}`;
	}
	return getBaseAuthUrl(group);
}

/**
 * Root of the group's SSO endpoints. The segment is the group id (ID_GROUP): these URLs are
 * registered at identity providers, and a name could change under them, the id cannot. The
 * server (rp-ms-auth BaseSsoService::groupSegment) builds them the same way.
 */
export function getSSOUrl(group: AccountingGroup): string {
	return `${getBaseAuthUrl(group)}/sso/${group.id_group}`;
}

export function getFederationMetadataUrl(group: AccountingGroup, version: string = '2007-06'): string {
	return `${getSSOUrl(group)}/FederationMetadata/${version}/FederationMetadata.xml`;
}

export function getSamlLoginUrl(group: AccountingGroup): string {
	return `${getSSOUrl(group)}/saml2`;
}

/** OpenID Connect login of the group */
export function getOidcLoginUrl(group: AccountingGroup): string {
	return `${getSSOUrl(group)}/oidc`;
}

/** The redirect URI to register at the OpenID Connect provider */
export function getOidcCallbackUrl(group: AccountingGroup): string {
	return `${getSSOUrl(group)}/oidc/callback`;
}

/** The redirect URI of a preset: fixed for every group using it, on the preset's deployment */
export function getOidcPresetCallbackUrl(preset: SsoOidcPreset): string {
	return `${preset.base_url}/auth/sso/${preset.id}/oidc/callback`;
}

/**
 * Where the customer's Entra admin consents to a preset's application in their tenant - the tenant
 * taken from the group's issuer (https://login.microsoftonline.com/{tenant}/v2.0); null when the
 * issuer names no Entra tenant yet
 */
export function getEntraAdminConsentUrl(issuer: string | null | undefined, clientId: string): string | null {
	const tenant = /^https:\/\/(?:login\.microsoftonline\.com\/([0-9a-f-]{36})\/v2\.0|sts\.windows\.net\/([0-9a-f-]{36}))\/?$/i.exec((issuer ?? '').trim());
	if (!tenant) return null;
	return `https://login.microsoftonline.com/${tenant[1] ?? tenant[2]}/adminconsent?client_id=${encodeURIComponent(clientId)}`;
}

/** The back-channel logout URI to register at the OpenID Connect provider */
export function getOidcBackchannelLogoutUrl(group: AccountingGroup): string {
	return `${getSSOUrl(group)}/oidc/backchannel-logout`;
}

/**
 * The front-channel logout URI to register at the OpenID Connect provider (rp-ms-auth LogoutRoute:
 * the provider loads it in an iframe with iss and sid) - also the plain logout link of the deployment
 */
export function getLogoutUrl(group: AccountingGroup): string {
	return `${getBaseAuthUrl(group)}/logout`;
}

/** The post-logout redirect URI to register at the OpenID Connect provider: the login page */
export function getPostLogoutRedirectUri(group: AccountingGroup): string {
	return getBaseAuthUrl(group);
}

/** The public API root of the group (the gateway serves /api next to /ms at the host root): its custom domain, otherwise this origin */
export function getApiBaseUrl(group?: AccountingGroup): string {
	if (group?.custom_domain) {
		return `https://${group.custom_domain}`;
	}
	return window.location.origin;
}

/**
 * SCIM 2.0 base URL ("Tenant URL") of the group. Users and roles are group-level objects and every
 * request carries a token bound to an accounting of the group, so the server derives the tenant
 * from the token - no accounting in the URL.
 */
export function getScimBaseUrl(group: AccountingGroup): string {
	return `${getApiBaseUrl(group)}/api/v2/scim`;
}
